import throttle from "lodash.throttle";
import { EVENTS, VIEWPORTS } from "../../constants";

export default class ResizeObserver {
  /**
   * @param {HTMLElement} element
   * @param {boolean} isThrottled
   * @param {number} delay
   */
  constructor(element, isThrottled = true, delay = 300) {
    this.element = element;
    this.isThrottled = isThrottled;
    this.delay = delay;
    this.currentViewport = null;
    this.scrollbarWidthCustomProperty = "--scrollbar-width";

    // Set correct scope for callbacks
    this._onResize = this._onResize.bind(this);
    this._handleResize = this._handleResize.bind(this);

    // initial call
    this._handleResize();

    // Add resize event listener
    this.element.addEventListener("resize", this._onResize());
  }

  /**
   * call `_handleResize()` via throttle if `isThrottled` is true
   *
   * @private
   * @returns {Function}
   */
  _onResize() {
    return this.isThrottled
      ? throttle(this._handleResize, this.delay)
      : this._handleResize;
  }

  /**
   * handles resize event
   */
  _handleResize() {
    this._dispatchResizeEvent();
    this._checkViewport();
    this._setScrollbarWidth();
  }

  /**
   * writes scroll bar width to custom property
   */
  _setScrollbarWidth() {
    const scrollbarWidth = window.innerWidth - document.body.clientWidth;
    const documentRoot = document.querySelector(":root");

    if (scrollbarWidth && documentRoot) {
      documentRoot.style.setProperty(
        this.scrollbarWidthCustomProperty,
        `${scrollbarWidth}px`
      );
    }
  }

  /**
   * checks for current viewport
   */
  _checkViewport() {
    const viewports = Object.keys(VIEWPORTS);
    const viewport = viewports.find((key) => {
      return this.element.matchMedia(VIEWPORTS[key]).matches;
    });

    if (viewport !== this.currentViewport) {
      this.currentViewport = viewport;
      this._dispatchViewportEvent(viewport);
    }
  }

  /**
   * dispatch `RESIZED` event
   *
   */
  _dispatchResizeEvent() {
    this.element.dispatchEvent(
      new CustomEvent(EVENTS.RESIZED, {
        detail: {
          width: this.element.innerWidth,
          height: this.element.innerHeight,
        },
      })
    );
  }

  /**
   * dispatch `VIEWPORT_CHANGED` event
   *
   * @param {string} viewport
   */
  _dispatchViewportEvent(viewport) {
    this.element.dispatchEvent(
      new CustomEvent(EVENTS.VIEWPORT_CHANGED, {
        detail: { viewport },
      })
    );
  }
}
